import { Gobbler } from '@groww-tech-pvt/gobbler';
import {
  EventRequestV2,
  UserContext,
} from '@groww-tech-pvt/gobbler-proto-ts/dist/EventRequestV2';
import { Gobbler as GobblerV2 } from '@groww-tech-pvt/gobbler-web-sdk';
import type {
  BatchConfig as GobblerV2BatchConfig,
  Config as GobblerV2Config,
  UserContext as UserContextV2,
} from '@groww-tech-pvt/gobbler-web-sdk';
import {
  getDeviceModelInfo,
  getNetworkType,
  getOSName,
  getOSVersion,
} from '@groww-tech-pvt/ella';

import { WEBSITE_VERSION } from 'src/utils/constants/websiteVersion';
import { ENVS, getDomainConfig, resolveEnvFlavour } from 'src/utils/config/domainConfig';
import { DATA_TYPE_PATTERN, VALID_JOURNEY_TYPES } from 'src/utils/constants';


// ─── Gobbler v1 (legacy, HTTP) ───

export const gb = new Gobbler({
  apikey: getDomainConfig().gobblerApiKey,
  baseUrl: getDomainConfig().gobblerApiUrl,
  eventRequestV2: EventRequestV2,
});

// ─── Gobbler v2 (batched, Service Worker) ───

const gobblerV2Config: GobblerV2Config & Partial<GobblerV2BatchConfig> = {
  apikey: getDomainConfig().gobblerV2ApiKey,
  baseUrl: getDomainConfig().gobblerV2ApiUrl,
  debug: false,
  maxBatchSize: 30,
  swFlushIntervalMs: 10000,
  maxRetries: 3,
  retryBackoffMs: 1000,
  maxIDBEvents: 1000,
  maxBufferSize: 100,
  maxEventRetries: 5,
  walFlushIntervalMs: 100,
  swUrl: '/gobbler-web-sw.js',
  inMemoryFlushIntervalMs: 1000,
  storageBackend: 'in-memory',
};

const gbV2 = new GobblerV2(gobblerV2Config);

// ─── V2 Initialization ───

let initV2Promise: Promise<void> | null = null;

/**
 * Initialize gobbler-v2.
 * Sets up IndexedDB, Service Worker, and WAL for batched event delivery.
 * Safe to call multiple times — deduplicates automatically.
 */
export async function initGobbler() {
  if (initV2Promise) {
    return initV2Promise;
  }

  initV2Promise = gbV2.init().catch(err => {
    console.warn('[GobblerV2] init() rejected:', err);
    initV2Promise = null;
  });

  return initV2Promise;
}

export function getDeviceContext() {
  return gb.getDeviceContext();
}

// Helper function to get basic device context data
function getBasicDeviceContextData() {
  const deviceModelInfo = getDeviceModelInfo();

  return {
    appVersionCode: 0,
    appVersionName: WEBSITE_VERSION,
    carrier: '', // Not available in browser context
    manufacturer: deviceModelInfo.manufacturer,
    model: deviceModelInfo.model,
    networkType: getNetworkType(),
    osName: getOSName() ?? '',
    osVersion: getOSVersion(),
    sdkVersion: '',
  };
}

// Initialize device context with basic data (deviceId will be empty initially)
function initializeDeviceContext() {
  const basicContext = getBasicDeviceContextData();

  const deviceCtx = {
    ...basicContext,
    deviceId: '', // Empty initially, can be updated later
  };

  gb.setDeviceContext(deviceCtx);
  gbV2.setDeviceContext(deviceCtx);
}

// Set device context automatically on module initialization
initializeDeviceContext();

// Auto-init v2 in browser context
if (typeof window !== 'undefined') {
  initGobbler();
}

export function setDeviceContext(deviceId: string) {
  const basicContext = getBasicDeviceContextData();

  const deviceCtx = {
    ...basicContext,
    deviceId: deviceId ?? '',
  };

  gb.setDeviceContext(deviceCtx);
  gbV2.setDeviceContext(deviceCtx);
}

export function setUserContext(userContext: UserContext) {
  gb.setUserContext(userContext);
  gbV2.setUserContext(userContext as UserContextV2);
}

type GobblerEvent = {
  encode: (message: any) => {
    finish: () => Uint8Array;
  };
};

function getBasicEventContext() {
  return {
    pageUrl: typeof window !== 'undefined' ? window?.location?.href : '',
  };
}

/**
 * Reads journeyType and dataType from the URL query string.
 * journeyType is validated against the JOURNEY_TYPES allowlist.
 * dataType accepts alphanumeric + underscore, max 100 chars.
 * Invalid or missing values resolve to ''.
 */
function getJourneyContextFromURL() {
  if (typeof window === 'undefined') {
    return {
      journeyType: '',
      dataType: '',
    };
  }

  const searchParams = new URLSearchParams(window.location.search);
  const rawJourneyType = searchParams.get('journeyType') ?? '';
  const rawDataType = searchParams.get('dataType') ?? '';

  return {
    journeyType: VALID_JOURNEY_TYPES.has(rawJourneyType) ? rawJourneyType : '',
    dataType: DATA_TYPE_PATTERN.test(rawDataType) ? rawDataType : '',
  };
}

/**
 * track
 *
 * @remarks
 * Sends events to gobbler v1 (HTTP) and gobbler-v2 (batched, SW) in parallel
 * during the rollout period. v2 events carry job="gb-batch" to differentiate
 * them on the backend. v1 will be deprecated once v2 is stable.
 *
 * Optionally also fires the event to Google Analytics.
 *
 * @param name - The name of the event in string
 * @param event - The event that has to be sent to gobbler
 * @param payload - The attributes that is to be sent with the event
 * @param sendToGA - Whether to send the event to Google Analytics (default: true)
 * ```
 */
export const trackEvent = async <T extends GobblerEvent>(
  eventName: string,
  event: T,
  payload: Parameters<T['encode']>[0],
  sendToGA: boolean = true
) => {
  const name = eventName;
  const flavour = resolveEnvFlavour();
  const journeyContext = getJourneyContextFromURL();
  const enrichedPayload = {
    ...journeyContext,
    ...payload,
  };

  if (flavour.includes(ENVS.NEXT) || flavour.includes(ENVS.UAT)) {
    console.log({ eventName, payload: enrichedPayload });
  }

  // Gobbler / GA are wired for production traffic only (use runtime host, not build-time FLAVOUR).
  if (flavour === ENVS.PROD) {
    try {
      // ── Proto encode as guard (sync) ──
      // Fails fast for invalid payloads. Prevents both V1 and V2 from
      // processing bad data.
      const encodedByteArray = event.encode(enrichedPayload).finish();
      const eventContext = getBasicEventContext();

      // ── v2: fire FIRST, before v1 await ──
      // V2 must run before any await so that page-navigation events
      // reach IDB before the page unloads.
      try {
        gbV2.track(name, enrichedPayload as Record<string, unknown>, eventContext);
      } catch (e) {
        console.error('[GobblerV2] Failed to send event', e);
      }

      // ── v1: HTTP POST with retry ──
      const GOBBLER_API_MAX_RETRIES = 2;
      let retryAttempts = 0;
      let isSuccess = false;

      while (retryAttempts <= GOBBLER_API_MAX_RETRIES && !isSuccess) {
        try {
          await gb.track(name, encodedByteArray, eventContext);
          isSuccess = true;
        } catch (e) {
          retryAttempts++;

          // Only capture the failure if we've exhausted all retries
          if (retryAttempts > GOBBLER_API_MAX_RETRIES) {
            console.error('Failed to send event to gobbler', e);
          }

          // Exponential backoff for retries
          if (!isSuccess && retryAttempts <= GOBBLER_API_MAX_RETRIES) {
            // captureGobblerBackoffTriggered(name, payload, retryAttempts);
            await new Promise(resolve =>
              setTimeout(resolve, 1000 * retryAttempts)
            );
          }
        }
      }

      if (sendToGA) {
        sendGAEvent(name, enrichedPayload);
      }
    } catch (e) {
      //error for encoding failed
      console.error(e);
    }
  }
};

// Type definitions for gtag
declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}

type GAValue = string | number | boolean;

const MAX_PARAM_LENGTH = 100; // GA4 safe-ish
const MAX_STRING_LENGTH = 100; // per param (GA limit is ~100 chars in UI)


function truncate(str: string, max = MAX_STRING_LENGTH) {
  return str.length > max ? str.slice(0, max) : str;
}

function flatten(
  value: unknown,
  prefix: string,
  output: Record<string, GAValue>
) {
  if (value == null) return;

  // Primitive values
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    output[prefix] = typeof value === 'string' ? truncate(value) : value;
    return;
  }

  // Array → stringify safely
  if (Array.isArray(value)) {
    output[`${prefix}_count`] = value.length;
    output[prefix] = truncate(value.join('|'));
    return;
  }

  // Object → recurse
  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, val]) => {
      const nextKey = prefix ? `${prefix}_${key}` : key;
      flatten(val, nextKey, output);
    });
  }
}

export function normalizePayload(
  payload: Record<string, unknown>
): Record<string, GAValue> {
  const result: Record<string, GAValue> = {};

  flatten(payload, '', result);

  // GA allows max ~25 params → keep important ones
  return Object.fromEntries(Object.entries(result).slice(0, MAX_PARAM_LENGTH));
}


/**
 * Send a custom event to Google Analytics
 */
export const sendGAEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  const env = resolveEnvFlavour();
  if (env.includes(ENVS.NEXT) || env.includes(ENVS.UAT)) {
    console.log({ eventName, eventParams });
  }

  if (env === ENVS.PROD) {
    if (typeof window !== 'undefined' && window.gtag) {
      const params = normalizePayload(eventParams ?? {});
      window.gtag('event', eventName, params);
    }
  }
};
